import { Box, CircularProgress, useTheme } from '@mui/material';
import { ComponentType, FC, SyntheticEvent, useRef } from 'react';
import AutoSizer from 'react-virtualized-auto-sizer';
import { FixedSizeList, ListChildComponentProps, VariableSizeList } from 'react-window';
import InfiniteLoader from 'react-window-infinite-loader';

import { UserMin, UserMinListParams } from '@apiContract';

import { sxList } from '../../styles';
import { UserMap } from '../../types';
import UserSearchField from '../UserSearchField';
import AllUsersItem from './AllUsersItem';
import SelectedUsersItem from './SelectedUsersItem';
import { useUserList } from './hook';

type ListProps = {
  deletedUsers: UserMap;
  selectedUsers: UserMap;
  onItemClick: (user: UserMin) => void;
  filters?: UserMinListParams;
  isSelected: boolean;
  model: string;
  skipRequest?: boolean;
  noItemsSetMessage?: string;
  filterOwner?: boolean;
};

type ListRefType = {
  resetAfterIndex: (index: number, shouldForceUpdate?: boolean) => void;
};

const sxCenterBox = { display: 'flex', justifyContent: 'center', alignItems: 'flex-start', flex: 1 };

const errorComponent = (search: string, model: string, noItemsSetMessage?: string) => (
  <Box sx={sxCenterBox}>{search ? 'No results found' : noItemsSetMessage || `No ${model.toLowerCase()} set`}</Box>
);

const getItemSize = (newItemsCount: number) => (index: number) => {
  if (newItemsCount) {
    if (newItemsCount === 1 && index === 0) return 95;
    if (index === 0) return 78;
    if (index === newItemsCount - 1) return 77;
  }
  return 60;
};

const UserItem = ({ style, index, data }: ListChildComponentProps) => {
  const user = data.items[index];
  const userId = user.id;

  if (!userId || !user) {
    return null;
  }

  return data.isAllUsersTab ? (
    <AllUsersItem index={index} style={style} selected={data.selectedUsers[userId]} user={user} />
  ) : (
    <SelectedUsersItem
      isNewBadge={data.newItemsCount && index === 0}
      index={index}
      divider={data.newItemsCount && index === data.newItemsCount - 1}
      style={style}
      selected={!data.deletedUsers[userId] || data.selectedUsers[userId]}
      user={user}
    />
  );
};

const UsersList: FC<ListProps> = ({
  deletedUsers,
  selectedUsers,
  onItemClick,
  filters = {},
  isSelected = false,
  model,
  skipRequest,
  noItemsSetMessage,
  filterOwner = false,
}) => {
  const listRef = useRef<ListRefType | null>(null);
  const sizeRef = useRef<number>(0);
  const { items, isLoading, loadNextPage, isItemLoaded, onSearch, search, searchDebounce } = useUserList({
    filters,
    skipRequest,
    filterOwner,
  });
  const theme = useTheme();

  const selectedArray = Object.values(selectedUsers);
  const filteredSelected = isSelected
    ? selectedArray.filter(({ displayName }) => displayName.toLowerCase().includes(searchDebounce.toLowerCase()))
    : selectedArray;

  const itemsToShow = isSelected ? [...filteredSelected, ...items] : items;

  const itemCount = itemsToShow.length;

  const isDynamicHeight = isSelected && selectedArray.length;

  const ListComponent = (isDynamicHeight ? VariableSizeList : FixedSizeList) as ComponentType<any>;
  const itemSize = isDynamicHeight ? getItemSize(filteredSelected.length) : 60;

  if (isDynamicHeight && filteredSelected.length !== sizeRef.current) {
    sizeRef.current = filteredSelected.length;
    listRef.current?.resetAfterIndex(0);
  }

  const onAutosizerClick = (event: SyntheticEvent<HTMLElement>) => {
    let target = event.target as HTMLElement;

    while (target && (!target.dataset.userindex || target.dataset.stop === 'stop')) {
      target = target.parentElement as HTMLElement;

      if (!target || target === event.currentTarget) {
        return;
      }
    }

    const index = target?.dataset.userindex;
    if (!index) return;
    onItemClick(itemsToShow[+index]);
  };

  return (
    <>
      <UserSearchField search={search} handleSearchChange={onSearch} />

      <Box sx={{ ...sxList(theme) }}>
        {isLoading && (
          <Box sx={sxCenterBox}>
            <CircularProgress />
          </Box>
        )}

        {!isLoading && !itemsToShow.length && errorComponent(search, model, noItemsSetMessage)}

        {!isLoading && !!itemsToShow.length && (
          <AutoSizer data-stop="stop" onClick={onAutosizerClick}>
            {({ height, width }) => {
              return (
                <InfiniteLoader
                  isItemLoaded={isItemLoaded}
                  itemCount={itemCount + 1}
                  loadMoreItems={loadNextPage}
                  threshold={50}
                >
                  {({ onItemsRendered, ref }) => {
                    return (
                      <ListComponent
                        height={height}
                        width={width}
                        onItemsRendered={onItemsRendered}
                        itemCount={itemCount}
                        itemSize={itemSize}
                        ref={(list: ListRefType) => {
                          ref(list);
                          listRef.current = list;
                        }}
                        itemData={{
                          newItemsCount: filteredSelected.length,
                          deletedUsers,
                          selectedUsers,
                          items: itemsToShow,
                          isAllUsersTab: !isSelected,
                        }}
                        className="customScrollBar"
                      >
                        {UserItem}
                      </ListComponent>
                    );
                  }}
                </InfiniteLoader>
              );
            }}
          </AutoSizer>
        )}
      </Box>
    </>
  );
};

export default UsersList;
